import { EVC_ADDRESS } from '@/config/euler-contracts'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useCallback, useMemo, useState } from 'react'
import { type Address, erc20Abi } from 'viem'
import { useAccount, usePublicClient, useReadContracts, useWriteContract } from 'wagmi'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { delegationTokenAbi } from '../../../../abis/evm/delegationToken'
import { EULER_CARD_SUB_ACCOUNT_ID, type MarketResolver, getActiveCardConfig } from '../constants'
import { useVaultClassifier } from '../contexts/VaultClassifierContext'
import type { UserCard } from './useUserCards'
import { useCreditReceiver } from './useCreditReceiver'
import { useSolverOptions } from './useSolverOptions'

export interface CardDelegationInfo {
  cardId: string
  cardLabel: string | null
  last4: string
  solverType: string
  delegationType: string
  displayName: string
  isActive: boolean
  isLoading: boolean
  revokeKey: string
}

export interface RevokeDelegationsResult {
  delegations: CardDelegationInfo[]
  isLoading: boolean
  isRevoking: boolean
  revokingKey: string | null
  revokeError: Error | null
  revoke: (key: string) => Promise<void>
}

interface RevokeTarget {
  key: string
  address: Address
  abi: readonly unknown[]
  functionName: string
  args: readonly unknown[]
}

function buildRevokeTargets(
  cards: UserCard[],
  userAddress: Address,
  operatorAddress: Address | undefined,
  debtTokenMap: Record<string, string>,
  aTokenMap: Record<string, string>,
  resolveMarket?: MarketResolver,
): RevokeTarget[] {
  if (!operatorAddress) return []

  const targets: RevokeTarget[] = []

  for (const card of cards) {
    const config = getActiveCardConfig(card, resolveMarket)

    switch (config.delegationType) {
      case 'euler':
      case 'euler-withdraw': {
        const subAccountId = config.eulerSubAccountId ?? EULER_CARD_SUB_ACCOUNT_ID
        const subAccount = getSubAccountAddress(userAddress, subAccountId)
        targets.push({
          key: `euler-${card.cardID}-${subAccountId}`,
          address: EVC_ADDRESS as Address,
          abi: eulerVaultConnectorAbi,
          functionName: 'setAccountOperator',
          args: [subAccount, operatorAddress, false],
        })
        break
      }
      case 'pooled': {
        const borrowToken = card.solverConfig?.borrowTokenAddress as string | undefined
        const debtToken = borrowToken ? debtTokenMap[borrowToken.toLowerCase()] : undefined
        if (debtToken) {
          targets.push({
            key: `pooled-${card.cardID}`,
            address: debtToken as Address,
            abi: delegationTokenAbi,
            functionName: 'approveDelegation',
            args: [operatorAddress, 0n],
          })
        }
        break
      }
      case 'spend-allowance': {
        const baseToken = (card.solverConfig?.baseTokenAddress ?? card.solverConfig?.borrowTokenAddress) as
          | string
          | undefined
        const aToken = baseToken ? aTokenMap[baseToken.toLowerCase()] : undefined
        if (aToken) {
          targets.push({
            key: `spend-${card.cardID}`,
            address: aToken as Address,
            abi: erc20Abi,
            functionName: 'approve',
            args: [operatorAddress, 0n],
          })
        }
        break
      }
    }
  }

  return targets
}

export function useRevokeDelegations(cards: UserCard[]): RevokeDelegationsResult {
  const { address } = useAccount()
  const publicClient = usePublicClient()
  const { writeContractAsync } = useWriteContract()
  const { resolveMarket } = useVaultClassifier()

  const {
    hypurrFiDebtTokenByBorrowToken,
    hypurrFiATokenByBaseToken,
    isLoading: isSolverLoading,
  } = useSolverOptions()

  const { creditReceiver: operatorAddress, isLoading: isLoadingOperator } = useCreditReceiver()

  const [revokingKey, setRevokingKey] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState<Error | null>(null)
  const [revokedKeys, setRevokedKeys] = useState<Set<string>>(new Set())

  const revokeTargets = useMemo(
    () =>
      address
        ? buildRevokeTargets(
            cards,
            address,
            operatorAddress,
            hypurrFiDebtTokenByBorrowToken,
            hypurrFiATokenByBaseToken,
            resolveMarket,
          )
        : [],
    [cards, address, operatorAddress, hypurrFiDebtTokenByBorrowToken, hypurrFiATokenByBaseToken, resolveMarket],
  )

  const readContracts = useMemo(() => {
    if (!address || !operatorAddress) return []

    return revokeTargets.map((target) => {
      switch (target.abi) {
        case eulerVaultConnectorAbi:
          return {
            address: target.address,
            abi: eulerVaultConnectorAbi,
            functionName: 'isAccountOperatorAuthorized' as const,
            args: [target.args[0] as Address, operatorAddress] as const,
          }
        case delegationTokenAbi:
          return {
            address: target.address,
            abi: delegationTokenAbi,
            functionName: 'borrowAllowance' as const,
            args: [address, operatorAddress] as const,
          }
        default:
          return {
            address: target.address,
            abi: erc20Abi,
            functionName: 'allowance' as const,
            args: [address, operatorAddress] as const,
          }
      }
    })
  }, [address, operatorAddress, revokeTargets])

  const { data: readResults, isLoading: isReadLoading } = useReadContracts({
    contracts: readContracts,
    query: {
      enabled: readContracts.length > 0,
      refetchInterval: 15_000,
    },
  })

  const delegations = useMemo((): CardDelegationInfo[] => {
    return revokeTargets.map((target, i) => {
      const card = cards.find((c) => target.key.includes(c.cardID))
      const config = card ? getActiveCardConfig(card, resolveMarket) : undefined
      const result = readResults?.[i]
      let isActive = false

      if (revokedKeys.has(target.key)) {
        isActive = false
      } else if (result?.status === 'success') {
        if (typeof result.result === 'boolean') {
          isActive = result.result
        } else if (typeof result.result === 'bigint') {
          isActive = result.result > 0n
        }
      }

      return {
        cardId: card?.cardID ?? '',
        cardLabel: card?.label ?? null,
        last4: card?.last4 ?? '',
        solverType: card?.solverType ?? '',
        delegationType: config?.delegationType ?? 'none',
        displayName: config?.displayName ?? card?.solverType ?? '',
        isActive,
        isLoading: isReadLoading,
        revokeKey: target.key,
      }
    })
  }, [revokeTargets, readResults, isReadLoading, cards, revokedKeys, resolveMarket])

  const revoke = useCallback(
    async (key: string) => {
      const target = revokeTargets.find((t) => t.key === key)
      if (!target || !publicClient) return

      setRevokingKey(key)
      setRevokeError(null)

      try {
        const hash = await writeContractAsync({
          address: target.address,
          abi: target.abi as any,
          functionName: target.functionName as any,
          args: target.args as any,
        })

        await waitForTxReceipt(publicClient, hash)
        setRevokedKeys((prev) => new Set([...prev, key]))
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.includes('rejected') || msg.includes('denied') || msg.includes('cancelled')) {
          setRevokeError(new Error('Transaction rejected'))
        } else {
          setRevokeError(err instanceof Error ? err : new Error(msg))
        }
      } finally {
        setRevokingKey(null)
      }
    },
    [revokeTargets, writeContractAsync, publicClient],
  )

  return {
    delegations,
    isLoading: isSolverLoading || isReadLoading || isLoadingOperator,
    isRevoking: revokingKey !== null,
    revokingKey,
    revokeError,
    revoke,
  }
}
